/**
 * CitationsList Component
 *
 * Displays a list of citations with document information and metadata.
 * Designed to be used within the Citations tab of the ArtefactsPanel.
 */

import React from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "react-i18next";
import {
  Input,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownPopover,
  DropdownItem,
} from "@heroui/react";
import { useDebouncedValue } from "@webuild-ai/react-ui/hooks";

import { Citation } from "@/types/citations";
import { useArtefactStore } from "@/stores/ArtefactStore";

import { CitationDocumentViewer } from "../Citations/CitationDocumentViewer";

type GroupingOption = "none" | "message" | "chronological";
type SortOrder = "asc" | "desc";

interface CitationGroup {
  key: string;
  title: string;
  subtitle?: string;
  citations: Citation[];
}

/** Captures the number in a label such as "C12", "[C12]" or "12". */
const LABEL_NUMBER_PATTERN = /\[?C?(\d+)\]?/;
/** Finds the first run of digits in a group key. */
const DIGITS_PATTERN = /\d+/;

/** Citations per "By Message" group. */
const MESSAGE_GROUP_SIZE = 5;
/** Citations per "By Time" range. */
const CHRONOLOGICAL_RANGE_SIZE = 10;

const ALL_GROUP_KEY = "all";
const ALL_GROUP_TITLE = "All Citations";

/** The number in a citation label, or 0 when the label carries none. */
const parseLabelNumber = (label: string): number => {
  const labelMatch = label.match(LABEL_NUMBER_PATTERN);

  return labelMatch ? parseInt(labelMatch[1], 10) : 0;
};

/** The first number in a group key, or 0 when it carries none. */
const parseGroupKeyNumber = (groupKey: string): number => {
  return parseInt(groupKey.match(DIGITS_PATTERN)?.[0] || "0", 10);
};

const compareNumbers = (a: number, b: number, sortOrder: SortOrder): number => {
  return sortOrder === "asc" ? a - b : b - a;
};

/** Sorts in place by label, as both grouped modes do within each group. */
const sortByLabel = (citations: Citation[], sortOrder: SortOrder): Citation[] => {
  return citations.sort((a, b) => {
    return sortOrder === "asc" ? a.label.localeCompare(b.label) : b.label.localeCompare(a.label);
  });
};

/** Orders by the number in the label, used as a proxy for chronological order. */
const sortChronologically = (citations: Citation[], sortOrder: SortOrder): Citation[] => {
  return citations.sort((a, b) => {
    return compareNumbers(parseLabelNumber(a.label), parseLabelNumber(b.label), sortOrder);
  });
};

/** Buckets citations by a key, keeping the order in which each key first appears. */
const bucketCitations = (
  citations: Citation[],
  keyFor: (citation: Citation) => string,
): Map<string, Citation[]> => {
  const groups = new Map<string, Citation[]>();

  citations.forEach((citation) => {
    const groupKey = keyFor(citation);

    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)!.push(citation);
  });

  return groups;
};

const buildAllGroup = (citations: Citation[]): CitationGroup[] => {
  return [{ key: ALL_GROUP_KEY, title: ALL_GROUP_TITLE, citations }];
};

/** Group by citation label ranges (every five citations). */
const messageGroupKey = (citation: Citation): string => {
  const citationNum = parseLabelNumber(citation.label);
  const rangeStart = Math.floor(citationNum / MESSAGE_GROUP_SIZE) * MESSAGE_GROUP_SIZE;

  return `Group ${rangeStart + 1}-${rangeStart + MESSAGE_GROUP_SIZE}`;
};

const groupByMessage = (citations: Citation[], sortOrder: SortOrder): CitationGroup[] => {
  const groups = bucketCitations(citations, messageGroupKey);

  return Array.from(groups.entries()).map(([groupKey, grouped]) => {
    return {
      key: groupKey,
      title: `${groupKey} (${grouped.length} citations)`,
      subtitle: `Citation range`,
      citations: sortByLabel(grouped, sortOrder),
    };
  });
};

/** Group into ranges of ten: 1-10, 11-20 and so on, since citations carry no timestamps. */
const chronologicalGroupKey = (citation: Citation): string => {
  const citationNum = parseLabelNumber(citation.label);
  const rangeStart =
    Math.floor((citationNum - 1) / CHRONOLOGICAL_RANGE_SIZE) * CHRONOLOGICAL_RANGE_SIZE + 1;
  const rangeEnd = rangeStart + CHRONOLOGICAL_RANGE_SIZE - 1;

  return `Citations ${rangeStart}-${rangeEnd}`;
};

const groupByTime = (citations: Citation[], sortOrder: SortOrder): CitationGroup[] => {
  const groups = bucketCitations(citations, chronologicalGroupKey);

  // Sort groups by citation range
  const sortedGroups = Array.from(groups.entries()).sort(([dateA], [dateB]) => {
    return compareNumbers(parseGroupKeyNumber(dateA), parseGroupKeyNumber(dateB), sortOrder);
  });

  return sortedGroups.map(([dateKey, grouped]) => ({
    key: dateKey,
    title: dateKey,
    subtitle: `${grouped.length} citation${grouped.length !== 1 ? "s" : ""}`,
    citations: sortByLabel(grouped, sortOrder),
  }));
};

/**
 * Groups and sorts citations for display. Chronological mode orders the flat list
 * first and then groups it, so the order within a group is overridden by the label sort.
 */
const groupCitations = (
  filteredCitations: Citation[],
  groupBy: GroupingOption,
  sortOrder: SortOrder,
): CitationGroup[] => {
  const sortedCitations = [...filteredCitations];

  if (groupBy === "chronological") {
    sortChronologically(sortedCitations, sortOrder);
  }

  if (groupBy === "message") {
    return groupByMessage(sortedCitations, sortOrder);
  }

  if (groupBy === "chronological") {
    return groupByTime(sortedCitations, sortOrder);
  }

  return buildAllGroup(sortedCitations);
};

/** Keeps the first citation for each label and filename pair, in order. */
const deduplicateCitations = (citations: Citation[]): Citation[] => {
  const seen = new Map<string, Citation>();

  citations.forEach((citation) => {
    const key = `${citation.label}-${citation.filename}`;

    if (!seen.has(key)) {
      seen.set(key, citation);
    }
  });

  return Array.from(seen.values());
};

/** Citations whose filename, excerpt or label contains the search term. */
const filterCitations = (citations: Citation[], searchTerm: string): Citation[] => {
  if (!searchTerm) {
    return citations;
  }

  const searchLower = searchTerm.toLowerCase();

  return citations.filter((citation) => {
    const searchableText =
      `${citation.filename} ${citation.snippet || citation.text} ${citation.label}`.toLowerCase();

    return searchableText.includes(searchLower);
  });
};

/** The label in square brackets, unless it already carries them. */
const bracketedLabel = (label: string): string => (label.startsWith("[") ? label : `[${label}]`);

const CitationsEmptyState: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="h-full flex items-center justify-center p-6">
      <div className="text-center text-default-500">
        <div className="text-4xl mb-3">📄</div>
        <h3 className="text-lg font-semibold mb-2">{t("No citations found")}</h3>
        <p className="text-sm text-default-400 max-w-sm">
          {t("Document references and citations from AI responses will appear here.")}
        </p>
      </div>
    </div>
  );
};

interface CitationSearchBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
}

const CitationSearchBar: React.FC<CitationSearchBarProps> = ({ searchTerm, onSearchChange }) => {
  const { t } = useTranslation();

  return (
    <div className="p-3 border-b border-divider">
      <div className="flex items-center gap-2">
        <Icon
          className="text-default-400 pointer-events-none flex-shrink-0"
          icon="solar:search-outline"
          width={20}
        />
        <div className="relative flex-1">
          <Input
            className="w-full h-9 pr-8"
            placeholder={t("Search citations...")}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          {searchTerm && (
            <Button
              isIconOnly
              aria-label={t("Clear search")}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 min-w-0"
              data-testid="clear-search"
              size="sm"
              variant="ghost"
              onPress={() => onSearchChange("")}
            >
              <Icon className="text-default-400" icon="solar:close-circle-linear" width={16} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

interface GroupingControlsProps {
  groupBy: GroupingOption;
  sortOrder: SortOrder;
  citationCount: number;
  onGroupingChange: (key: React.Key) => void;
  onToggleSortOrder: () => void;
}

const GroupingControls: React.FC<GroupingControlsProps> = ({
  groupBy,
  sortOrder,
  citationCount,
  onGroupingChange,
  onToggleSortOrder,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between px-3 py-2 border-b border-divider bg-default-50">
      <div className="flex items-center gap-2">
        <Dropdown>
          <DropdownTrigger className="inline-flex items-center justify-center gap-2 px-3 h-8 text-sm rounded-medium hover:bg-default-100">
            <Icon icon="solar:menu-dots-outline" width={16} />
            {groupBy === "none" && t("No Grouping")}
            {groupBy === "message" && t("By Message")}
            {groupBy === "chronological" && t("By Time")}
            <Icon icon="solar:alt-arrow-down-linear" width={14} />
          </DropdownTrigger>
          <DropdownPopover>
            <DropdownMenu
              aria-label={t("Grouping options")}
              selectedKeys={[groupBy]}
              selectionMode="single"
              onAction={onGroupingChange}
            >
              <DropdownItem key="none">
                <div className="flex items-center gap-2">
                  <Icon icon="solar:list-outline" width={16} />
                  {t("No Grouping")}
                </div>
              </DropdownItem>
              <DropdownItem key="message">
                <div className="flex items-center gap-2">
                  <Icon icon="solar:chat-dots-outline" width={16} />
                  {t("Group by Message")}
                </div>
              </DropdownItem>
              <DropdownItem key="chronological">
                <div className="flex items-center gap-2">
                  <Icon icon="solar:calendar-outline" width={16} />
                  {t("Group by Time")}
                </div>
              </DropdownItem>
            </DropdownMenu>
          </DropdownPopover>
        </Dropdown>

        <Button
          isIconOnly
          aria-label={sortOrder === "asc" ? t("Sort descending") : t("Sort ascending")}
          className="h-8"
          size="sm"
          variant="ghost"
          onPress={onToggleSortOrder}
        >
          {/* Both orders share one glyph; descending is the same icon rotated. */}
          <Icon
            className={sortOrder === "desc" ? "transform rotate-180" : ""}
            icon="solar:sort-by-alphabet-outline"
            width={16}
          />
        </Button>
      </div>

      <span className="text-xs text-default-500">
        {citationCount} citation{citationCount !== 1 ? "s" : ""}
      </span>
    </div>
  );
};

const NoSearchResults: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-center h-full p-6">
      <div className="text-center text-default-500">
        <Icon
          className="text-4xl mb-3 mx-auto"
          icon="solar:search-minimalistic-linear"
          width={48}
        />
        <h3 className="text-lg font-semibold mb-2">{t("No results found")}</h3>
        <p className="text-sm text-default-400 max-w-sm">
          {t("Try adjusting your search terms or clear the search to see all citations.")}
        </p>
      </div>
    </div>
  );
};

interface CitationCardProps {
  citation: Citation;
  onSelect: (citation: Citation) => void;
}

const CitationCard: React.FC<CitationCardProps> = ({ citation, onSelect }) => (
  <div
    className="p-3 bg-default-50 rounded-lg hover:bg-default-100 transition-colors cursor-pointer"
    role="button"
    tabIndex={0}
    onClick={() => onSelect(citation)}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect(citation);
      }
    }}
  >
    <div className="flex items-start justify-between mb-2">
      <span className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs font-medium">
        {bracketedLabel(citation.label)}
      </span>
      <Icon
        className="text-default-400 ml-2 flex-shrink-0"
        icon="solar:document-text-outline"
        width={16}
      />
    </div>
    <h4 className="text-sm font-medium text-default-700 mb-1 line-clamp-1">{citation.filename}</h4>
    <p className="text-xs text-default-500 line-clamp-2 leading-relaxed">
      {citation.snippet || citation.text}
    </p>
  </div>
);

interface CitationGroupSectionProps {
  group: CitationGroup;
  showHeader: boolean;
  isCollapsed: boolean;
  onToggle: (groupKey: string) => void;
  onSelect: (citation: Citation) => void;
}

const CitationGroupSection: React.FC<CitationGroupSectionProps> = ({
  group,
  showHeader,
  isCollapsed,
  onToggle,
  onSelect,
}) => (
  <div className="space-y-3">
    {showHeader && (
      <div className="flex items-center justify-between">
        <button
          className="flex items-center gap-2 text-sm font-medium text-default-700 hover:text-default-900 transition-colors"
          onClick={() => onToggle(group.key)}
        >
          <Icon
            icon={isCollapsed ? "solar:alt-arrow-right-linear" : "solar:alt-arrow-down-linear"}
            width={16}
          />
          <span>{group.title}</span>
          <span className="text-xs text-default-400">({group.citations.length})</span>
        </button>
        {group.subtitle && <span className="text-xs text-default-400">{group.subtitle}</span>}
      </div>
    )}

    {!isCollapsed && (
      <div className={group.key !== "all" ? "ml-4 space-y-3" : "space-y-3"}>
        {group.citations.map((citation, index) => (
          <CitationCard
            key={`${citation.label}-${index}`}
            citation={citation}
            onSelect={onSelect}
          />
        ))}
      </div>
    )}
  </div>
);

/** The set with the key added when absent and removed when present. */
const toggledKey = (keys: Set<string>, key: string): Set<string> => {
  const next = new Set(keys);

  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }

  return next;
};

export const CitationsList: React.FC = () => {
  const { getCitationsForCurrentChat } = useArtefactStore();
  const allCitations = getCitationsForCurrentChat();

  // Search state
  const [searchTerm, setSearchTerm] = React.useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  // Grouping state
  const [groupBy, setGroupBy] = React.useState<GroupingOption>("none");
  const [sortOrder, setSortOrder] = React.useState<SortOrder>("desc");
  const [collapsedGroups, setCollapsedGroups] = React.useState<Set<string>>(new Set());

  // Deduplicate citations by filename and document_id
  const deduplicatedCitations = React.useMemo(
    () => deduplicateCitations(allCitations),
    [allCitations],
  );

  // Filter citations based on search term
  const filteredCitations = React.useMemo(
    () => filterCitations(deduplicatedCitations, debouncedSearchTerm),
    [deduplicatedCitations, debouncedSearchTerm],
  );

  // Group and sort citations
  const groupedCitations = React.useMemo(
    () => groupCitations(filteredCitations, groupBy, sortOrder),
    [filteredCitations, groupBy, sortOrder],
  );

  // Modal state for CitationDocumentViewer
  const [isViewerOpen, setIsViewerOpen] = React.useState(false);
  const [selectedCitation, setSelectedCitation] = React.useState<Citation | null>(null);

  // Show empty state only if there are no citations at all (not just filtered)
  if (deduplicatedCitations.length === 0) {
    return <CitationsEmptyState />;
  }

  const handleCitationClick = (citation: Citation) => {
    setSelectedCitation(citation);
    setIsViewerOpen(true);
  };

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((prev) => toggledKey(prev, groupKey));
  };

  const handleGroupingChange = (key: React.Key) => {
    setGroupBy(key as GroupingOption);
    setCollapsedGroups(new Set()); // Reset collapsed state when changing grouping
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  // A lone "all" group needs no header; any other group, or several, do.
  const hasSeveralGroups = groupedCitations.length > 1;

  return (
    <div className="h-full flex flex-col">
      <CitationSearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />

      {filteredCitations.length > 0 && (
        <GroupingControls
          citationCount={filteredCitations.length}
          groupBy={groupBy}
          sortOrder={sortOrder}
          onGroupingChange={handleGroupingChange}
          onToggleSortOrder={toggleSortOrder}
        />
      )}

      <div className="flex-1 overflow-y-auto">
        {filteredCitations.length === 0 ? (
          <NoSearchResults />
        ) : (
          <div className="p-3 space-y-4">
            {groupedCitations.map((group) => (
              <CitationGroupSection
                key={group.key}
                group={group}
                isCollapsed={collapsedGroups.has(group.key)}
                showHeader={group.key !== "all" || hasSeveralGroups}
                onSelect={handleCitationClick}
                onToggle={toggleGroup}
              />
            ))}
          </div>
        )}
      </div>

      {/* Citation Document Viewer Modal */}
      <CitationDocumentViewer
        citation={selectedCitation}
        isOpen={isViewerOpen}
        onOpenChange={setIsViewerOpen}
      />
    </div>
  );
};
